/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    constructor() {
      super();
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.results = document.getElementById('filter-results');

      if ((!this.filteringEnabled && !this.sortingEnabled) || !this.results) return;
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.resultsCountEl = document.getElementById('results-count');
      this.form = document.getElementById('facets');

      if (this.filteringEnabled) {
        this.showFiltersBtn = document.querySelector('.js-show-filters');
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = document.querySelector('.active-filters');
        this.footer = this.querySelector('.facets__footer');
        this.filtersOpen = this.dataset.filtersOpen;

        this.showFiltersBtn.addEventListener('click', this.showFilters.bind(this));
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', window.debounce(this.handleFilterChange.bind(this), 300));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
      }

      if (this.sortingEnabled) {
        this.desktopSortBy = document.getElementById('sort-by');
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
        // WebKit restores this select despite autocomplete="off"; the results are what the server rendered.
        const rendered = Array.from(this.desktopSortBy.options).find((option) => option.defaultSelected);
        if (rendered) this.desktopSortBy.value = rendered.value;
      }

      window.addEventListener('popstate', this.handleHistoryChange.bind(this));
      window.addEventListener('on:debounced-resize', this.handleWindowResize.bind(this));

      // Recently shown filter states, so going back to one (deselecting, the back
      // button) needs no new request. The state the page started in is fetched in
      // the background after the first change.
      this.cache = new Map();
      this.currentParams = this.form ? this.formParams() : null;
      // An entry without state that has this page load's URL shows this landing state.
      this.landingParams = this.currentParams;
      this.landingSearch = window.location.search;

      // A back step before this script ran left the page on the state it loaded with.
      const [navigation] = performance.getEntriesByType('navigation');
      if (this.form && navigation
        && FacetFilters.resultsKey(navigation.name) !== FacetFilters.resultsKey(window.location.href)) {
        this.landingParams = window.location.search.slice(1);
        this.applyFilters(this.landingParams, false);
      }

      this.handleWindowResize();
    }

    /**
     * Returns the filter/sort parameters of the current form state.
     * @returns {string}
     */
    formParams() {
      const searchParams = new URLSearchParams(new FormData(this.form));
      const emptyParams = [];

      // Remove duplicate 'sort_by' parameters.
      if (this.sortingEnabled && this.desktopSortBy) searchParams.set('sort_by', this.desktopSortBy.value);

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      return searchParams.toString();
    }

    /**
     * Returns a cache key that doesn't depend on the parameter order.
     * @param {string} searchParams - Filter/sort parameters.
     * @returns {string}
     */
    static cacheKey(searchParams) {
      const entries = Array.from(new URLSearchParams(searchParams).entries());
      entries.sort((a, b) => (a[0] + a[1] < b[0] + b[1] ? -1 : 1));
      return new URLSearchParams(entries).toString();
    }

    /**
     * Returns the parts of a URL that decide which results it shows.
     * @param {string} url - Full URL.
     * @returns {string}
     */
    static resultsKey(url) {
      const entries = Array.from(new URL(url).searchParams.entries())
        .filter(([key]) => key.startsWith('filter.') || key === 'sort_by' || key === 'page');
      return FacetFilters.cacheKey(new URLSearchParams(entries).toString());
    }

    /**
     * Returns a fresh cached page for the parameters, or null.
     * @param {string} searchParams - Filter/sort parameters.
     * @returns {?string}
     */
    cacheGet(searchParams) {
      const key = FacetFilters.cacheKey(searchParams);
      const entry = this.cache.get(key);
      if (!entry || Date.now() - entry.time > FacetFilters.CACHE_TTL) return null;
      // Mark as recently used; the age (TTL) still counts from the fetch.
      this.cache.delete(key);
      this.cache.set(key, entry);
      return entry.html;
    }

    /**
     * Stores a fetched page, keeping only the most recently used few (they are large).
     * @param {string} searchParams - Filter/sort parameters.
     * @param {string} html - Fetched page markup.
     */
    cacheSet(searchParams, html) {
      if (!html.includes('id="filter-results"')) return;
      const key = FacetFilters.cacheKey(searchParams);
      this.cache.delete(key);
      this.cache.set(key, { html, time: Date.now() });
      while (this.cache.size > FacetFilters.CACHE_SIZE) {
        this.cache.delete(this.cache.keys().next().value);
      }
    }

    /**
     * Fetches a filter state in the background so showing it later is instant.
     * @param {string} searchParams - Filter/sort parameters.
     */
    async prefetch(searchParams) {
      if (this.cacheGet(searchParams) !== null) return;
      try {
        const response = await fetch(`${window.location.pathname}?${searchParams}`, { priority: 'low' });
        if (response.ok) this.cacheSet(searchParams, await response.text());
      } catch (err) {
        // A failed prefetch only means the next visit fetches normally.
      }
    }

    /**
     * Opens the filters drawer.
     */
    showFilters() {
      this.open(this.showFiltersBtn);
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      if (this.sortingEnabled) {
        // Sync selected values of mobile and desktop 'sort by' dropdowns.
        if (evt.target === this.desktopSortBy) {
          const mobileSortBy = this.querySelector(`#drawer-sort-by input[name="sort_by"][value="${this.desktopSortBy.value}"]`);
          if (mobileSortBy) {
            mobileSortBy.checked = true;
          }
        } else if (evt.target.name === 'sort_by') {
          this.desktopSortBy.value = evt.target.value;
        }
      }

      this.applyFilters(this.formParams());
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString());
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString());
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      // Entries pushed by updateURL carry their parameters. An entry without state
      // shows the landing state only if it has this page load's URL (same key as its
      // prefetched copy); after a reload the landing state belongs to a later entry,
      // so an earlier entry goes by its own URL.
      let params = window.location.search.slice(1);
      if (evt && evt.state && typeof evt.state.searchParams === 'string') {
        params = evt.state.searchParams;
      } else if (window.location.search === this.landingSearch && this.landingParams !== null) {
        params = this.landingParams;
      }

      this.applyFilters(params, false);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleWindowResize() {
      if (!theme.mediaMatches.md) {
        this.querySelectorAll('.filter').forEach((el) => {
          el.open = false;
          el.classList.remove('is-open');
        });
      } else {
        this.querySelectorAll('.filter').forEach((el, index) => {
          if (this.filtersOpen === 'all' || (this.filtersOpen === 'some' && index < 5)) {
            el.open = true;
            el.classList.add('is-open');
          }
        });
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, updateUrl = true) {
      // Only the newest request may update the page (quick filters make fast taps likely).
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;

      // A history step to the state on screen (anchor, back and quickly forward): stopping
      // what loads is enough, unless the form was changed for that request.
      const previousParams = this.currentParams;
      const hasPrevious = previousParams !== null && previousParams !== undefined;
      const key = FacetFilters.cacheKey(searchParams);
      if (!updateUrl && hasPrevious && FacetFilters.cacheKey(previousParams) === key
        && FacetFilters.cacheKey(this.formParams()) === key) {
        this.results.classList.remove('is-loading');
        return;
      }

      this.results.classList.add('is-loading');

      try {
        // Fetch filtered products markup, unless this state was shown recently.
        let html = this.cache ? this.cacheGet(searchParams) : null;
        if (html !== null) {
          // Let the tap paint first (pressed chip, loading state), as with a fetch;
          // the timeout covers hidden tabs, where animation frames don't run.
          await new Promise((resolve) => {
            window.requestAnimationFrame(() => window.setTimeout(resolve, 0));
            window.setTimeout(resolve, 100);
          });
        } else {
          const response = await fetch(`${window.location.pathname}?${searchParams}`, { signal: controller.signal });
          if (response.ok) {
            html = await response.text();
            if (this.cache) this.cacheSet(searchParams, html);
          }
        }

        if (html !== null && controller === this.abortController) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;

          // Update the filters.
          if (this.filteringEnabled) this.updateFilters(tmpl.content);

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Keep the state we came from ready, so undoing this change is instant.
          this.currentParams = searchParams;
          if (this.cache && hasPrevious) this.prefetch(previousParams);
        }
      } catch (err) {
        if (err.name !== 'AbortError') throw err;
      } finally {
        if (controller === this.abortController) this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the filters with the fetched data.
     * @param {string} html - HTML of the fetched document.
     */
    updateFilters(html) {
      // Save current focus
      const currentFocusId = document.activeElement ? document.activeElement.id : null;

      // Save open filters
      const openedFilterIDs = [];
      this.querySelectorAll('.filter[open]').forEach((el) => openedFilterIDs.push(el.id));

      // Update filters
      this.filters.innerHTML = html.querySelector('.facets__filters').innerHTML;

      // Restore open filters
      openedFilterIDs.forEach((id) => {
        this.querySelectorAll(`#${CSS.escape(id)}`).forEach((el) => { el.open = true; });
      });

      // Preserve focus
      if (currentFocusId) {
        const focusEl = this.filters.querySelector(`#${CSS.escape(currentFocusId)}`);
        if (focusEl) focusEl.focus();
      }

      // Update total
      this.updateResultsTotal(html);

      // Update active filters.
      this.updateActiveFilters(html);

      // Update '[x] results' button (mobile only).
      const footerEl = html.querySelector('.facets__footer');
      this.footer.innerHTML = footerEl.innerHTML;

      // Lets add-ons re-apply their state: stock-filter-counts.js adjusts the counts,
      // quick-filters.js re-renders its chips from the fetched document.
      this.dispatchEvent(new CustomEvent('on:facet-filters:updated', { detail: { html } }));
    }

    /**
     * Updates the result count.
     * @param {string} html - HTML of the fetched page.
     */
    updateResultsTotal(html) {
      if (this.resultsCountEl) {
        this.resultsCountEl.innerHTML = html.querySelector('#results-count').innerHTML;
      }
    }

    /**
     * Updates the active filters.
     * @param {string} html - HTML of the fetched page.
     */
    updateActiveFilters(html) {
      this.activeFilters.innerHTML = html.querySelector('.active-filters').innerHTML;
      this.activeFilters.hidden = !this.activeFilters.querySelector('.active-filters__item');
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
  }

  // Five minutes keeps prices and stock reasonably fresh; four pages of roughly
  // 0.5-1 MB each keep memory modest on phones.
  FacetFilters.CACHE_TTL = 5 * 60 * 1000;
  FacetFilters.CACHE_SIZE = 4;

  customElements.define('facet-filters', FacetFilters);
}
